import { useCallback, useEffect, useRef, useState } from "react";

interface UseAutoProgressionProps {
  isActive: boolean;
  duration: number;
  onComplete: () => void;
  key?: string | number;
}

interface UseAutoProgressionReturn {
  progress: number;
  isPaused: boolean;
  pause: () => void;
  resume: () => void;
  reset: () => void;
}

export function useAutoProgression(props: UseAutoProgressionProps): UseAutoProgressionReturn {
  const { isActive, duration, onComplete, key } = props;

  const [progress, setProgress] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const startTimeRef = useRef<number>(0);
  const pausedTimeRef = useRef<number>(0);
  const animationFrameRef = useRef<number | undefined>(undefined);

  const pause = useCallback(() => {
    if (!isPaused) {
      setIsPaused(true);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = undefined;
      }
    }
  }, [isPaused]);

  const resume = useCallback(() => {
    if (isPaused) {
      setIsPaused(false);
    }
  }, [isPaused]);

  const reset = useCallback(() => {
    setProgress(0);
    setIsPaused(false);
    startTimeRef.current = 0;
    pausedTimeRef.current = 0;

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = undefined;
    }
  }, []);

  useEffect(() => {
    if (!isActive) {
      reset();
      return;
    }

    setProgress(0);
    setIsPaused(false);
    startTimeRef.current = Date.now();
    pausedTimeRef.current = 0;
  }, [isActive, duration, onComplete, key, reset]);

  useEffect(() => {
    if (!isActive || isPaused) {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = undefined;
      }

      return;
    }

    const updateProgress = () => {
      const currentTime = Date.now();
      const elapsed = Math.max(0, currentTime - startTimeRef.current - pausedTimeRef.current);

      const delayedElapsed = Math.max(0, elapsed - 500);
      const newProgress = Math.min(delayedElapsed / duration, 1);

      setProgress(newProgress);

      if (newProgress >= 1) {
        onComplete();
        return;
      }

      animationFrameRef.current = requestAnimationFrame(updateProgress);
    };

    animationFrameRef.current = requestAnimationFrame(updateProgress);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = undefined;
      }
    };
  }, [isActive, isPaused, duration, onComplete]);

  useEffect(() => {
    if (!isActive) {
      return;
    }

    if (isPaused) {
      const pauseStartTime = Date.now();

      return () => {
        pausedTimeRef.current += Date.now() - pauseStartTime;
      };
    }

    return undefined;
  }, [isPaused, isActive]);

  return {
    progress,
    isPaused,
    pause,
    resume,
    reset,
  };
}
